import { useEffect, useState } from "react";
import { GroupedTableGroup } from "./GroupedTable";
import { FilteredValuesProps } from "@cofano/cck";
import { FilterSettingsProviderProps } from "@cofano/cck/Filter/FilterSettingsProvider";
import { BOSSettingsProvider } from "components/utils/TableUtils";

type UseGroupedTableProps<T, > = {
	id: string;
	defaultSize?: number;
	onChange: (pagination: Pagination, filters: FilteredValuesProps) => Promise<{
		groups: GroupedTableGroup<T>[];
		totalElements: number;
	}>;
	paginationEnabled?: boolean;
	headerInFront?: boolean;
	size?: "medium" | "small" | "compact";
	spacingBetweenGroups?: number;
	defaultFilters?: FilteredValuesProps;
	settingsProvider?: BOSSettingsProvider;
};

type StateProps<T, > = {
	pagination: Pagination;
	groups: GroupedTableGroup<T>[];
	contentLoading: boolean;
	totalElements: number;
	filters: FilteredValuesProps;
	tableKey: number;
};

type Pagination = {
	page: number;
	size: number;
};

const useGroupedTable = <T, >(
	{
		id,
		defaultSize,
		onChange,
		paginationEnabled = true,
		headerInFront = false,
		size = "small",
		spacingBetweenGroups = 0,
		defaultFilters = undefined,
		settingsProvider,
	}: UseGroupedTableProps<T>) => {

	const filterId = `cckFilter_${id}`;
	const filterSettings = settingsProvider?.filter;
	const savedFilters = ((
		filterSettings
			? filterSettings.settings(filterId)
			: localStorage.getItem(filterId)
				? JSON.parse(localStorage.getItem(filterId) || "")
				: undefined
	) ?? {}) as FilterSettingsProviderProps;

	const getDefaultFilters = () => {
		if (savedFilters?.groups && savedFilters.selectedGroupIndex) {
			const fields = savedFilters.groups[savedFilters.selectedGroupIndex]?.fields || [];
			return fields.filter((item) => item.visible && item.value !== undefined && item.value !== null).map((item) => ({
				property: item.id,
				value: item.value,
			}));
		} else if (savedFilters?.fields) {
			return savedFilters.fields.filter((item) => item.visible && item.value !== undefined && item.value !== null).map((item) => ({
				property: item.id,
				value: item.value,
			}));
		} else {
			return defaultFilters ?? [];
		}
	};

	const [ state, setState ] = useState<StateProps<T>>({
		pagination: {
			page: 0,
			size: defaultSize || 25,
		},
		groups: [],
		contentLoading: true,
		totalElements: 0,
		filters: getDefaultFilters(),
		tableKey: 0,
	});

	const getData = async (useTableKey: boolean, useLoading: boolean, pagination: Pagination, filters: FilteredValuesProps) => {
		if (useLoading) {
			setState(oldState => ({
				...oldState,
				contentLoading: true,
				groups: [],
			}));
		}

		const result = await onChange(pagination, filters);
		setState(oldState => ({
			...oldState,
			groups: result.groups,
			totalElements: result.totalElements,
			contentLoading: false,
			tableKey: useTableKey ? (oldState.tableKey + 1) : oldState.tableKey,
		}));
	};

	useEffect(() => {
		getData(false, true, state.pagination, state.filters);
	}, [ state.pagination.page, state.pagination.size, state.filters, state.tableKey ]);

	return {
		tableProps: {
			headerInFront: headerInFront,
			size: size,
			spacingBetweenGroups: spacingBetweenGroups,
			groups: state.groups,
			contentLoading: state.contentLoading,
			paginationSettings: {
				page: state.pagination.page,
				size: state.pagination.size,
				goToPage: (page: number, size: number) => {
					setState(oldState => ({
						...oldState,
						pagination: {
							page: page,
							size: size,
						},
					}));
				},
				totalResults: state.totalElements,
				paginationEnabled: paginationEnabled,
			},
		},

		filterProps: {
			id: filterId,
			settingsProvider: filterSettings,
			onSearch: (values: FilteredValuesProps) => {
				setState(oldState => ({
					...oldState,
					pagination: {
						page: 0,
						size: oldState.pagination.size,
					},
					filters: values,
				}));
			},
			defaultOpen: true,
			controlledValues: state.filters,
		},

		refetch: () => {
			getData(false, true, state.pagination, state.filters);
		},

		refetchByWebsocket: () => {
			getData(false, false, state.pagination, state.filters);
		},
	};
};

export default useGroupedTable;
